/**
 * DRBFM API Service
 *
 * Handles all DRBFM-related API calls including:
 * - Session management (CRUD)
 * - Analysis execution (SSE streaming)
 * - Results retrieval
 * - HTML export
 */

import { api } from './api';
import {
  DRBFMSession,
  CreateSessionResponse,
  SessionListResponse,
  SessionResultsResponse,
  AnalyzeRequest,
  DRBFMProgressEvent,
  ConvertToVerificationRequest,
  ConvertToVerificationResponse,
  KnowledgeReference,
  DrawingEvidence,
  ReviewQuestion,
  PredefinedPartLabel,
  DesignChange,
  FailureMode,
  Countermeasure,
  DRBFMCase,
  DRBFMCaseDesignChange,
  DRBFMCaseInputData,
  DRBFMCasePrecheckItem,
  UsedPrecheckAnswer,
} from '../types';

// ==============================================================================
// API Base URL
// ==============================================================================

const DRBFM_BASE_URL = '/drbfm';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const firstPresent = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return undefined;
};

const mapOptionalNumber = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
};

const mapKnowledgeMetadata = (kr: any): Record<string, unknown> | undefined => {
  const metadata = kr.metadata ?? kr.evidence_metadata;
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata)
    ? metadata
    : undefined;
};

const mapDrawingEvidence = (value: any): DrawingEvidence | undefined => {
  if (!value || typeof value !== 'object' || !value.bbox) {
    return undefined;
  }

  const source = ['document_image', 'pdf_page', 'llm_inferred'].includes(value.source)
    ? value.source
    : 'llm_inferred';
  const bboxUnit = ['normalized', 'pixel', 'pdf_point'].includes(value.bbox.unit)
    ? value.bbox.unit
    : 'normalized';

  return {
    documentId: firstPresent(value.document_id, value.documentId) || '',
    imageId: firstPresent(value.image_id, value.imageId),
    pageNumber: mapOptionalNumber(value.page_number, value.pageNumber),
    label: firstPresent(value.label, value.title) || '図面根拠',
    confidence: Number(value.confidence ?? 0),
    source,
    bbox: {
      x: Number(value.bbox.x),
      y: Number(value.bbox.y),
      width: Number(value.bbox.width),
      height: Number(value.bbox.height),
      unit: bboxUnit,
    },
    reasoning: firstPresent(value.reasoning, value.description),
  };
};

const mapReviewQuestions = (value: any): ReviewQuestion[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((item) => item && typeof item.question === 'string' && item.question.trim())
    .map((item, index) => ({
      id: firstPresent(item.id) || `question-${index + 1}`,
      question: item.question,
      reason: firstPresent(item.reason),
      required: item.required !== false,
    }));
};

const isDisplayableKnowledgeTitle = (value?: string): boolean => {
  if (!value) return false;
  const normalized = value.trim();
  return normalized.length > 0
    && normalized.toLowerCase() !== 'unknown'
    && !UUID_PATTERN.test(normalized);
};

const mapKnowledgeReference = (kr: any): KnowledgeReference => {
  const documentId = firstPresent(kr.document_id, kr.documentId, kr.id) || '';
  const rawTitle = firstPresent(kr.document_title, kr.documentTitle, kr.title);
  const fileName = firstPresent(kr.file_name, kr.fileName, kr.filename);
  const fallbackTitle = fileName || rawTitle || documentId;

  return {
    documentId,
    title: isDisplayableKnowledgeTitle(rawTitle) ? rawTitle! : fallbackTitle,
    fileName,
    snippet: firstPresent(kr.excerpt, kr.snippet, kr.content_preview, kr.contentPreview),
    relevanceScore: kr.relevance_score ?? kr.relevanceScore ?? 0,
    category: kr.category,
    pageNumber: mapOptionalNumber(kr.page_number, kr.pageNumber),
    imageId: firstPresent(kr.image_id, kr.imageId),
    metadata: mapKnowledgeMetadata(kr),
  };
};

const firstArray = (...values: unknown[]): unknown[] | undefined => (
  values.find(Array.isArray) as unknown[] | undefined
);

const mapUsedPrecheckAnswers = (
  ...values: unknown[]
): UsedPrecheckAnswer[] => {
  const value = firstArray(...values);
  return value ? value as UsedPrecheckAnswer[] : [];
};

const mapUnknownAssumptions = (...values: unknown[]): string[] => {
  const value = firstArray(...values);
  return value ? value.filter((item): item is string => typeof item === 'string') : [];
};

const mapSession = (session: any): DRBFMSession => ({
  id: session.id,
  tenantId: session.tenant_id ?? session.tenantId,
  userId: session.user_id ?? session.userId,
  itemName: session.item_name ?? session.itemName,
  title: session.title,
  specOldDocumentId: session.spec_old_document_id ?? session.specOldDocumentId,
  specNewDocumentId: session.spec_new_document_id ?? session.specNewDocumentId,
  status: session.status,
  currentFlow: session.current_flow ?? session.currentFlow,
  flowProgress: session.flow_progress ?? session.flowProgress ?? {},
  createdAt: session.created_at ?? session.createdAt,
  updatedAt: session.updated_at ?? session.updatedAt,
  completedAt: session.completed_at ?? session.completedAt,
  htmlExportPath: session.html_export_path ?? session.htmlExportPath,
});

const mapSessionDesignChange = (dc: any): DesignChange => ({
  id: dc.id,
  sessionId: dc.session_id ?? dc.sessionId,
  category: dc.category,
  itemName: dc.item_name ?? dc.itemName,
  oldSpecification: dc.old_specification ?? dc.oldSpecification,
  newSpecification: dc.new_specification ?? dc.newSpecification,
  changeDescription: dc.change_description ?? dc.changeDescription,
  impactAreas: dc.impact_areas ?? dc.impactAreas ?? [],
  createdAt: dc.created_at ?? dc.createdAt,
});

const mapFailureMode = (fm: any): FailureMode => ({
  id: fm.id,
  sessionId: fm.session_id ?? fm.sessionId,
  designChangeId: fm.design_change_id ?? fm.designChangeId,
  failureDescription: fm.failure_description ?? fm.failureDescription,
  failureCause: fm.failure_cause ?? fm.failureCause,
  failureEffect: fm.failure_effect ?? fm.failureEffect,
  severity: fm.severity,
  severityScore: fm.severity_score ?? fm.severityScore,
  rpnScore: fm.rpn_score ?? fm.rpnScore,
  knowledgeReferences: (fm.knowledge_references ?? fm.knowledgeReferences ?? [])
    .map(mapKnowledgeReference),
  drawingEvidence: mapDrawingEvidence(fm.drawing_evidence ?? fm.drawingEvidence),
  reviewQuestions: mapReviewQuestions(fm.review_questions ?? fm.reviewQuestions),
  usedPrecheckAnswers: mapUsedPrecheckAnswers(
    fm.used_precheck_answers,
    fm.usedPrecheckAnswers,
  ),
  unknownAssumptions: mapUnknownAssumptions(
    fm.unknown_assumptions,
    fm.unknownAssumptions,
  ),
  cadAnchor: (fm.cad_anchor ?? fm.cadAnchor)
    ? {
      cadModelId: fm.cad_anchor?.cad_model_id ?? fm.cadAnchor?.cadModelId,
      partLabel: fm.cad_anchor?.part_label ?? fm.cadAnchor?.partLabel,
      position: (fm.cad_anchor?.position ?? fm.cadAnchor?.position) as [number, number, number],
      confidence: fm.cad_anchor?.confidence ?? fm.cadAnchor?.confidence,
      source: fm.cad_anchor?.source ?? fm.cadAnchor?.source,
      reasoning: fm.cad_anchor?.reasoning ?? fm.cadAnchor?.reasoning,
    }
    : undefined,
  createdAt: fm.created_at ?? fm.createdAt,
});

const mapCountermeasure = (cm: any): Countermeasure => ({
  id: cm.id,
  failureModeId: cm.failure_mode_id ?? cm.failureModeId,
  sessionId: cm.session_id ?? cm.sessionId,
  countermeasureType: cm.countermeasure_type ?? cm.countermeasureType,
  description: cm.description,
  effectivenessScore: cm.effectiveness_score ?? cm.effectivenessScore,
  priority: cm.priority,
  createdAt: cm.created_at ?? cm.createdAt,
});

const mapSessionResults = (response: any): SessionResultsResponse => {
  if (!response.session) {
    throw new Error('DRBFM results response did not include a session.');
  }

  return {
    session: mapSession(response.session),
    designChanges: (response.design_changes ?? response.designChanges ?? []).map(mapSessionDesignChange),
    failureModes: (response.failure_modes ?? response.failureModes ?? []).map(mapFailureMode),
    countermeasures: (response.countermeasures ?? []).map(mapCountermeasure),
  };
};

export const mapCaseDesignChange = (dc: any): DRBFMCaseDesignChange => {
  const source = dc.source && typeof dc.source === 'object' && !Array.isArray(dc.source)
    ? dc.source
    : {};

  return {
    id: dc.id,
    caseId: dc.case_id ?? dc.caseId,
    category: dc.category,
    itemName: dc.item_name ?? dc.itemName,
    oldSpecification: dc.old_specification ?? dc.oldSpecification ?? '',
    newSpecification: dc.new_specification ?? dc.newSpecification ?? '',
    changeDescription: dc.change_description ?? dc.changeDescription ?? '',
    impactAreas: dc.impact_areas ?? dc.impactAreas ?? [],
    confidence: dc.confidence ?? null,
    source,
    sortOrder: dc.sort_order ?? dc.sortOrder ?? 0,
    createdAt: dc.created_at ?? dc.createdAt,
  };
};

export const mapCasePrecheckItem = (item: any): DRBFMCasePrecheckItem => ({
  id: item.id,
  caseId: item.case_id ?? item.caseId,
  question: item.question,
  reason: item.reason ?? '',
  requiredContextKey: item.required_context_key ?? item.requiredContextKey,
  answerStatus: item.answer_status ?? item.answerStatus ?? 'unanswered',
  answerText: item.answer_text ?? item.answerText ?? null,
  sortOrder: item.sort_order ?? item.sortOrder ?? 0,
  createdAt: item.created_at ?? item.createdAt,
  updatedAt: item.updated_at ?? item.updatedAt,
});

export const mapCase = (value: any): DRBFMCase => ({
  id: value.id,
  tenantId: value.tenant_id ?? value.tenantId,
  userId: value.user_id ?? value.userId,
  linkedSessionId: value.linked_session_id ?? value.linkedSessionId ?? null,
  title: value.title,
  productName: value.product_name ?? value.productName,
  targetPart: value.target_part ?? value.targetPart,
  changeSummary: value.change_summary ?? value.changeSummary,
  sourceDocumentIds: value.source_document_ids ?? value.sourceDocumentIds ?? [],
  context: value.context ?? {},
  status: value.status,
  designChanges: (value.design_changes ?? value.designChanges ?? []).map(mapCaseDesignChange),
  precheckItems: (value.precheck_items ?? value.precheckItems ?? []).map(mapCasePrecheckItem),
  createdAt: value.created_at ?? value.createdAt,
  updatedAt: value.updated_at ?? value.updatedAt,
  completedAt: value.completed_at ?? value.completedAt ?? null,
});

const caseDesignChangeToRequest = (change: DRBFMCaseDesignChange) => ({
  id: change.id || undefined,
  category: change.category,
  item_name: change.itemName,
  old_specification: change.oldSpecification,
  new_specification: change.newSpecification,
  change_description: change.changeDescription,
  impact_areas: change.impactAreas || [],
  confidence: change.confidence ?? null,
  source: change.source || {},
  sort_order: change.sortOrder || undefined,
});

const precheckAnswerToRequest = (item: DRBFMCasePrecheckItem) => ({
  id: item.id,
  answer_status: item.answerStatus,
  answer_text: item.answerText ?? null,
});

// ==============================================================================
// Types
// ==============================================================================

export interface CreateSessionParams {
  itemName: string;
  title?: string;
  specOldDocumentId?: string;
  specNewDocumentId?: string;
}

export interface ListSessionsParams {
  limit?: number;
  offset?: number;
}

export interface AnalysisCallbacks {
  onProgress: (event: DRBFMProgressEvent) => void;
  onComplete: () => void;
  onError: (error: string) => void;
}

export interface LinkCadRequest {
  cadModelId: string;
  predefinedPartLabels?: PredefinedPartLabel[];
}

export interface LinkCadResponse {
  linkedCount: number;
  unresolvedFailureModeIds: string[];
}

// ==============================================================================
// Session Management
// ==============================================================================

/**
 * Create a new DRBFM session
 */
export const createSession = async (
  params: CreateSessionParams
): Promise<CreateSessionResponse> => {
  // Convert to snake_case for backend API
  const request = {
    item_name: params.itemName,
    title: params.title,
    spec_old_document_id: params.specOldDocumentId,
    spec_new_document_id: params.specNewDocumentId,
  };

  // Transform response back to camelCase
  const response = await api.post<any>(`${DRBFM_BASE_URL}/sessions`, request);
  return {
    id: response.id,
    itemName: response.item_name,
    title: response.title,
    status: response.status,
    currentFlow: response.current_flow,
    createdAt: response.created_at,
  };
};

/**
 * List DRBFM sessions with pagination
 */
export const listSessions = async (
  params: ListSessionsParams = {}
): Promise<SessionListResponse> => {
  const { limit = 20, offset = 0 } = params;

  const response = await api.get<any>(
    `${DRBFM_BASE_URL}/sessions?limit=${limit}&offset=${offset}`
  );

  // Map snake_case API response to camelCase DRBFMSession[]
  // Backend returns item_name/created_at/completed_at; UI reads itemName/createdAt/completedAt.
  return {
    sessions: (response.sessions || []).map((s: any): DRBFMSession => ({
      id: s.id,
      tenantId: s.tenant_id,
      userId: s.user_id,
      itemName: s.item_name,
      title: s.title,
      specOldDocumentId: s.spec_old_document_id,
      specNewDocumentId: s.spec_new_document_id,
      status: s.status,
      currentFlow: s.current_flow,
      flowProgress: s.flow_progress || {},
      createdAt: s.created_at,
      updatedAt: s.updated_at,
      completedAt: s.completed_at,
      htmlExportPath: s.html_export_path,
    })),
    total: response.total,
    limit: response.limit,
    offset: response.offset,
  };
};

/**
 * Get a specific DRBFM session by ID
 */
export const getSession = async (sessionId: string): Promise<DRBFMSession> => {
  // API returns SessionDetailResponse which maps to DRBFMSession
  const response = await api.get<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}`);

  // Map snake_case API response to camelCase DRBFMSession
  return {
    id: response.id,
    tenantId: response.tenant_id,
    userId: response.user_id,
    itemName: response.item_name,
    title: response.title,
    specOldDocumentId: response.spec_old_document_id,
    specNewDocumentId: response.spec_new_document_id,
    status: response.status,
    currentFlow: response.current_flow,
    flowProgress: response.flow_progress || {},
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    completedAt: response.completed_at,
    htmlExportPath: response.html_export_path,
  };
};

/**
 * Get session results (design changes, failure modes, countermeasures)
 */
export const getSessionResults = async (
  sessionId: string
): Promise<SessionResultsResponse> => {
  const response = await api.get<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}/results`);
  return mapSessionResults(response);
};

/**
 * Delete a DRBFM session
 */
export const deleteSession = async (sessionId: string): Promise<void> => {
  await api.delete(`${DRBFM_BASE_URL}/sessions/${sessionId}`);
};

/**
 * Link an existing DRBFM session's failure modes to a CAD model.
 */
export const linkCad = async (
  sessionId: string,
  request: LinkCadRequest,
): Promise<LinkCadResponse> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}/link-cad`, {
    cad_model_id: request.cadModelId,
    predefined_part_labels: request.predefinedPartLabels || [],
  });

  return {
    linkedCount: response.linked_count,
    unresolvedFailureModeIds: response.unresolved_failure_mode_ids || [],
  };
};

// ==============================================================================
// Case Workflow
// ==============================================================================

export const createCase = async (
  params: DRBFMCaseInputData,
): Promise<DRBFMCase> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/cases`, {
    title: params.title,
    product_name: params.productName,
    target_part: params.targetPart,
    change_summary: params.changeSummary,
    source_document_ids: params.sourceDocumentIds || [],
    context: params.context || {},
  });

  return mapCase(response);
};

export const extractCaseChanges = async (
  caseId: string,
): Promise<DRBFMCaseDesignChange[]> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/cases/${caseId}/extract-changes`);
  return (response.design_changes || []).map(mapCaseDesignChange);
};

export const saveCaseDesignChanges = async (
  caseId: string,
  changes: DRBFMCaseDesignChange[],
): Promise<DRBFMCaseDesignChange[]> => {
  const response = await api.put<any>(`${DRBFM_BASE_URL}/cases/${caseId}/design-changes`, {
    design_changes: changes.map(caseDesignChangeToRequest),
  });
  return (response.design_changes || []).map(mapCaseDesignChange);
};

export const generateCasePrecheck = async (
  caseId: string,
): Promise<DRBFMCasePrecheckItem[]> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/cases/${caseId}/precheck`);
  return (response.precheck_items || []).map(mapCasePrecheckItem);
};

export const saveCasePrecheckAnswers = async (
  caseId: string,
  items: DRBFMCasePrecheckItem[],
): Promise<DRBFMCasePrecheckItem[]> => {
  const response = await api.put<any>(`${DRBFM_BASE_URL}/cases/${caseId}/precheck/answers`, {
    answers: items.map(precheckAnswerToRequest),
  });
  const precheckItems = response.case?.precheck_items;
  if (!Array.isArray(precheckItems)) {
    throw new Error(
      'DRBFM case precheck save response did not include case.precheck_items array.',
    );
  }
  return precheckItems.map(mapCasePrecheckItem);
};

export const analyzeCase = async (
  caseId: string,
): Promise<{ caseId: string; sessionId: string; status: string }> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/cases/${caseId}/analyze`);
  const sessionId = firstPresent(
    response.session?.id,
    response.session_id,
    response.case?.linked_session_id,
    response.case?.linkedSessionId,
  );

  if (!sessionId) {
    throw new Error('DRBFM case analysis response did not include a session id.');
  }

  return {
    caseId: firstPresent(response.case?.id, response.case_id) || caseId,
    sessionId,
    status: firstPresent(response.case?.status, response.session?.status) || '',
  };
};

export const getCaseResults = async (
  caseId: string,
): Promise<SessionResultsResponse> => {
  const response = await api.get<any>(`${DRBFM_BASE_URL}/cases/${caseId}/results`);
  return mapSessionResults(response);
};

// ==============================================================================
// Analysis Execution (SSE Streaming)
// ==============================================================================

export const buildAnalyzeUrl = (
  sessionId: string,
  request: Pick<AnalyzeRequest, 'cadModelId'> = {},
): string => {
  const baseURL = process.env.REACT_APP_API_URL || '';
  const params = new URLSearchParams();
  if (request.cadModelId) {
    params.set('cad_model_id', request.cadModelId);
  }
  const query = params.toString();
  return `${baseURL}${DRBFM_BASE_URL}/sessions/${sessionId}/analyze${query ? `?${query}` : ''}`;
};

/**
 * DRBFM SSE Client for streaming analysis progress
 */
export class DRBFMSSEClient {
  private abortController: AbortController | null = null;
  private isManualDisconnect = false;

  /**
   * Start analysis and stream progress updates.
   * Uses fetch() with Authorization header instead of EventSource
   * to avoid exposing JWT in URL query string (#453).
   */
  public startAnalysis(
    sessionId: string,
    request: AnalyzeRequest,
    callbacks: AnalysisCallbacks
  ): void {
    this.disconnect();

    try {
      const url = this.buildAnalysisURL(sessionId, request);
      this.abortController = new AbortController();
      this.isManualDisconnect = false;

      this.fetchStream(url, callbacks);
    } catch (error) {
      console.error('Failed to start DRBFM analysis stream:', error);
      callbacks.onError(error instanceof Error ? error.message : 'Failed to start analysis');
    }
  }

  /**
   * Disconnect the SSE connection
   */
  public disconnect(): void {
    if (this.abortController) {
      this.isManualDisconnect = true;
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Check if currently connected
   */
  public isConnected(): boolean {
    return this.abortController !== null && !this.abortController.signal.aborted;
  }

  /**
   * Build the analysis URL (no token in URL)
   */
  private buildAnalysisURL(sessionId: string, request: AnalyzeRequest): string {
    const baseURL = process.env.REACT_APP_API_URL || '';

    const params = new URLSearchParams({
      old_specification: request.oldSpecification,
      new_specification: request.newSpecification,
    });
    if (request.cadModelId) {
      params.set('cad_model_id', request.cadModelId);
    }

    return `${baseURL}${DRBFM_BASE_URL}/sessions/${sessionId}/analyze?${params.toString()}`;
  }

  /**
   * Fetch-based SSE stream with Authorization header
   */
  private async fetchStream(url: string, callbacks: AnalysisCallbacks): Promise<void> {
    try {
      const token = sessionStorage.getItem('access_token');
      const headers: Record<string, string> = {
        'Accept': 'text/event-stream',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        headers,
        signal: this.abortController?.signal,
      });

      if (!response.ok) {
        this.abortController = null;
        if (response.status === 401 || response.status === 403) {
          callbacks.onError('Authentication failed. Please log in again.');
        } else {
          callbacks.onError(`Analysis request failed: ${response.status}`);
        }
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        this.abortController = null;
        callbacks.onError('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let receivedTerminal = false;

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data) {
                try {
                  const parsed = JSON.parse(data);
                  if (parsed.type === 'complete' || parsed.type === 'error') {
                    receivedTerminal = true;
                  }
                  this.handleMessage(parsed, callbacks);
                } catch (e) {
                  // Skip unparseable lines
                }
              }
            }
          }
        }

        if (!receivedTerminal && !this.isManualDisconnect) {
          callbacks.onError('Analysis stream ended unexpectedly. Please try again.');
          this.disconnect();
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      if (this.isManualDisconnect) return;
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.error('DRBFM SSE stream error:', error);
      callbacks.onError('Connection lost. Please try again.');
    }
  }

  /**
   * Handle incoming SSE messages
   */
  private handleMessage(data: any, callbacks: AnalysisCallbacks): void {
    switch (data.type) {
      case 'connected':
        console.log('DRBFM analysis connected:', data.session_id);
        break;

      case 'flow_start':
      case 'flow_progress':
      case 'flow_complete':
      case 'cad_link_complete':
      case 'cad_link_failed':
        callbacks.onProgress({
          type: data.type,
          flow: data.flow || 5,
          message: data.message || (
            data.type === 'cad_link_failed'
              ? `CADリンクに失敗しました: ${data.error_code || 'unknown'}`
              : `CADリンク完了: ${data.linked_count || 0}件リンク`
          ),
          progress: data.progress ?? 0.98,
          data: data.data || {
            linkedCount: data.linked_count,
            unresolvedFailureModeIds: data.unresolved_failure_mode_ids,
            errorCode: data.error_code,
          },
          timestamp: data.timestamp || new Date().toISOString(),
        });
        break;

      case 'complete':
        callbacks.onProgress({
          type: 'complete',
          flow: 5,
          message: data.message || 'Analysis completed',
          progress: 1.0,
          data: data.data,
          timestamp: data.timestamp,
        });
        callbacks.onComplete();
        this.disconnect();
        break;

      case 'error':
        callbacks.onError(data.message || 'Analysis failed');
        this.disconnect();
        break;

      default:
        console.warn('Unknown DRBFM SSE message type:', data.type);
    }
  }
}

/**
 * Alternative: Start analysis using POST with fetch (for large specifications)
 * This handles the case where specifications are too large for URL parameters
 */
export const startAnalysisWithFetch = async (
  sessionId: string,
  request: AnalyzeRequest,
  callbacks: AnalysisCallbacks,
  abortSignal?: AbortSignal,
): Promise<void> => {
  const token = sessionStorage.getItem('access_token');

  try {
    const response = await fetch(
      buildAnalyzeUrl(sessionId, request),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          old_specification: request.oldSpecification,
          new_specification: request.newSpecification,
          predefined_part_labels: request.predefinedPartLabels || [],
        }),
        signal: abortSignal,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE format: "data: {...}\n\n"
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            handleProgressEvent(data, callbacks);
          } catch (e) {
            console.error('Failed to parse SSE data:', e);
          }
        }
      }
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    callbacks.onError(error instanceof Error ? error.message : 'Analysis failed');
  }
};

/**
 * Handle progress event from fetch-based SSE
 */
export const handleProgressEvent = (data: any, callbacks: AnalysisCallbacks): void => {
  switch (data.type) {
    case 'connected':
      console.log('DRBFM analysis connected');
      break;

    case 'flow_start':
    case 'flow_progress':
    case 'flow_complete':
      callbacks.onProgress({
        type: data.type,
        flow: data.flow,
        message: data.message,
        progress: data.progress,
        data: data.data,
        timestamp: data.timestamp,
      });
      break;

    case 'cad_link_complete': {
      const linkedCount = Number(data.linked_count || 0);
      const unresolvedFailureModeIds = data.unresolved_failure_mode_ids || [];
      const unresolvedCount = unresolvedFailureModeIds.length;
      callbacks.onProgress({
        type: 'cad_link_complete',
        flow: 5,
        message: unresolvedCount > 0
          ? `CADリンク完了: ${linkedCount}件リンク、${unresolvedCount}件未解決`
          : `CADリンク完了: ${linkedCount}件リンク`,
        progress: 0.98,
        data: {
          linkedCount,
          unresolvedFailureModeIds,
        },
        timestamp: data.timestamp || new Date().toISOString(),
      });
      break;
    }

    case 'cad_link_failed': {
      const errorCode = data.error_code || 'unknown';
      callbacks.onProgress({
        type: 'cad_link_failed',
        flow: 5,
        message: `CADリンクに失敗しました: ${errorCode}`,
        progress: 0.98,
        data: { errorCode },
        timestamp: data.timestamp || new Date().toISOString(),
      });
      break;
    }

    case 'complete':
      callbacks.onProgress({
        type: 'complete',
        flow: 5,
        message: data.message || 'Analysis completed',
        progress: 1.0,
        data: data.data,
        timestamp: data.timestamp,
      });
      callbacks.onComplete();
      break;

    case 'error':
      callbacks.onError(data.message || 'Analysis failed');
      break;
  }
};

// ==============================================================================
// Export
// ==============================================================================

/**
 * Export session results as HTML
 * Returns a Blob with correct content type
 */
export const exportSessionAsHTML = async (sessionId: string): Promise<Blob> => {
  const baseURL = process.env.REACT_APP_API_URL || '';
  const token = sessionStorage.getItem('access_token');

  const response = await fetch(
    `${baseURL}${DRBFM_BASE_URL}/sessions/${sessionId}/export`,
    {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }

  // Get the blob and ensure it has the correct type
  const blob = await response.blob();

  // Re-create blob with explicit type if needed
  if (blob.type !== 'text/html') {
    return new Blob([blob], { type: 'text/html' });
  }

  return blob;
};

/**
 * Sanitize filename for safe download
 * Ensures .html extension and handles special characters
 */
const sanitizeFilename = (name: string): string => {
  // Ensure the filename has .html extension
  let safeName = name;
  if (!safeName.toLowerCase().endsWith('.html')) {
    safeName = `${safeName}.html`;
  }

  // Replace characters that are problematic in filenames
  safeName = safeName.replace(/[<>:"/\\|?*]/g, '_');

  return safeName;
};

/**
 * Download exported HTML file
 */
export const downloadExportedHTML = async (
  sessionId: string,
  filename?: string
): Promise<void> => {
  const blob = await exportSessionAsHTML(sessionId);

  // Ensure filename has .html extension
  const safeFilename = sanitizeFilename(filename || `drbfm_${sessionId}.html`);

  // Create blob URL
  const blobUrl = URL.createObjectURL(blob);

  // Create and trigger download
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = safeFilename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);

  // Delay revoking to ensure download starts
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 1000);
};

/**
 * Export session results as Excel
 * Returns a Blob with correct content type
 */
export const exportSessionAsExcel = async (sessionId: string): Promise<Blob> => {
  const baseURL = process.env.REACT_APP_API_URL || '';
  const token = sessionStorage.getItem('access_token');

  const response = await fetch(
    `${baseURL}${DRBFM_BASE_URL}/sessions/${sessionId}/export/excel`,
    {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }

  return response.blob();
};

/**
 * Download exported Excel file
 */
export const downloadExportedExcel = async (
  sessionId: string,
  filename?: string
): Promise<void> => {
  const blob = await exportSessionAsExcel(sessionId);

  // Ensure filename has .xlsx extension
  let safeFilename = filename || `drbfm_${sessionId}.xlsx`;
  if (!safeFilename.toLowerCase().endsWith('.xlsx')) {
    safeFilename = `${safeFilename}.xlsx`;
  }
  safeFilename = safeFilename.replace(/[<>:"/\\|?*]/g, '_');

  // Create blob URL
  const blobUrl = URL.createObjectURL(blob);

  // Create and trigger download
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = safeFilename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);

  // Delay revoking to ensure download starts
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 1000);
};

/**
 * Get HTML preview content for a session
 */
export const getSessionHTMLPreview = async (sessionId: string): Promise<string> => {
  const response = await api.get<{ html: string }>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/preview`
  );
  return response.html;
};

// ==============================================================================
// DRBFM → Verification Conversion
// ==============================================================================

/**
 * Convert DRBFM failure modes to verification items
 */
export const convertToVerification = async (
  sessionId: string,
  request: ConvertToVerificationRequest,
): Promise<ConvertToVerificationResponse> => {
  const response = await api.post<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/convert-to-verification`,
    {
      failure_mode_ids: request.failureModeIds,
      verification_session_id: request.verificationSessionId || null,
      verification_unit_id: request.verificationUnitId || null,
      new_session_title: request.newSessionTitle || null,
      new_session_model_name: request.newSessionModelName || null,
      new_unit_number: request.newUnitNumber || null,
      new_unit_name: request.newUnitName || null,
    },
  );

  return {
    verificationSessionId: response.verification_session_id,
    verificationUnitId: response.verification_unit_id,
    itemsCreated: response.items_created,
    items: response.items || [],
  };
};

// ==============================================================================
// Service Object Export
// ==============================================================================

export const drbfmService = {
  // Session management
  createSession,
  listSessions,
  getSession,
  getSessionResults,
  deleteSession,
  linkCad,

  // Case workflow
  createCase,
  extractCaseChanges,
  saveCaseDesignChanges,
  generateCasePrecheck,
  saveCasePrecheckAnswers,
  analyzeCase,
  getCaseResults,

  // Analysis
  DRBFMSSEClient,
  startAnalysisWithFetch,
  buildAnalyzeUrl,

  // Export
  exportSessionAsHTML,
  downloadExportedHTML,
  exportSessionAsExcel,
  downloadExportedExcel,

  // Preview
  getSessionHTMLPreview,

  // Conversion
  convertToVerification,
};

export default drbfmService;
